import React from 'react';

const AttachReportModal = ({ isOpen, onClose, report }) => {
    if (!isOpen || !report) return null;

    const { tasksCloned, epicResolution, positionResolution, dependencyResolution } = report;

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                        Add-in Attach Report
                    </h2>
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                    {/* Tasks cloned summary */}
                    {tasksCloned && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Tasks Cloned</h3>
                            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                                <span className="font-medium">{tasksCloned.mainTasks}</span> main task{tasksCloned.mainTasks !== 1 ? 's' : ''}
                                {' and '}
                                <span className="font-medium">{tasksCloned.subtasks}</span> subtask{tasksCloned.subtasks !== 1 ? 's' : ''}
                                {' cloned into the project.'}
                            </div>
                        </div>
                    )}

                    {/* Epic resolution */}
                    {epicResolution && epicResolution.length > 0 && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Epic Resolution</h3>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 border-b">
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Epic Name</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Resolved To</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {epicResolution.map((entry, idx) => (
                                        <tr key={idx} className="border-b border-gray-100">
                                            <td className="px-3 py-2 text-gray-700">{entry.epicName}</td>
                                            <td className="px-3 py-2 text-gray-600">
                                                {entry.status === 'matched' ? entry.resolvedTo : 'Unmatched'}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                                    entry.status === 'matched'
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-amber-100 text-amber-700'
                                                }`}>
                                                    {entry.status === 'matched' ? 'Matched' : 'Unmatched'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Position placement */}
                    {positionResolution && positionResolution.length > 0 && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Position Placement</h3>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 border-b">
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Task Name</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Placement</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {positionResolution.map((entry, idx) => (
                                        <tr key={idx} className="border-b border-gray-100">
                                            <td className="px-3 py-2 text-gray-700">{entry.taskName}</td>
                                            <td className="px-3 py-2 text-gray-600">
                                                {entry.placement !== undefined && entry.placement !== null
                                                    ? `Position ${entry.placement}`
                                                    : '-'}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                                    entry.status === 'perfect'
                                                        ? 'bg-green-100 text-green-700'
                                                        : entry.status === 'non-perfect'
                                                            ? 'bg-amber-100 text-amber-700'
                                                            : 'bg-red-100 text-red-700'
                                                }`}>
                                                    {entry.status === 'perfect'
                                                        ? 'Perfect'
                                                        : entry.status === 'non-perfect'
                                                            ? 'Non-perfect'
                                                            : 'No match'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Dependency resolution */}
                    {dependencyResolution && dependencyResolution.length > 0 && (
                        <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Dependency Resolution</h3>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-gray-50 border-b">
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Subtask</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Referenced Name</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Direction</th>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dependencyResolution.map((entry, idx) => (
                                        <tr key={idx} className="border-b border-gray-100">
                                            <td className="px-3 py-2 text-gray-700">{entry.subtaskName}</td>
                                            <td className="px-3 py-2 text-gray-600">{entry.referencedName}</td>
                                            <td className="px-3 py-2 text-gray-600">{entry.direction}</td>
                                            <td className="px-3 py-2">
                                                <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                                                    entry.status === 'resolved'
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-red-100 text-red-700'
                                                }`}>
                                                    {entry.status === 'resolved' ? 'Resolved' : 'Unresolved'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm font-medium"
                    >
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AttachReportModal;
